import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '../../../providers/Auth';
import { authApiService } from '../services/authApi';
import { refUrlService } from '../services/refUrlService';
// Direct file import — the campaign barrel imports from @/features/auth,
// so going through it here would create a circular dependency
import { campaignApiService } from '@/features/campaign/services/campaignApi';
import { firebaseAuthService } from '../../../lib/firebase/auth';
import type {
  RequestOtpCodeRequest,
  RequestOtpCodeResponse,
  SignUpByOtpCodeRequest,
  SignUpByOtpCodeResponse,
  RequestAppLinkRequest,
  RequestAppLinkResponse,
} from '../types/auth';
import { useSignalR } from '@/providers/SignalR';
import { trackingService } from '@/lib/tracking';

interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  requestOtp: (
    request: RequestOtpCodeRequest
  ) => Promise<RequestOtpCodeResponse>;
  signUp: (request: SignUpByOtpCodeRequest) => Promise<SignUpByOtpCodeResponse>;
  logout: () => void;
  isRequestingOtp: boolean;
  isSigningUp: boolean;
  requestOtpError: Error | null;
  signUpError: Error | null;
  setAuthPhoneNumber: (phoneNumber: string) => void;
  authPhoneNumber: string | null;
  requestAppDownloadLink: (
    request: RequestAppLinkRequest
  ) => Promise<RequestAppLinkResponse>;
}

export const useAuth = (): UseAuthReturn => {
  const authContext = useAuthContext();
  if (!authContext) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  const {
    signIn,
    logout,
    isAuthenticated,
    isLoading,
    setAuthPhoneNumber,
    authPhoneNumber,
  } = authContext;
  const queryClient = useQueryClient();
  const { stopConnection } = useSignalR();

  const requestOtpMutation = useMutation({
    mutationFn: (request: RequestOtpCodeRequest) =>
      authApiService.requestOtpCode(request),
  });

  const signUpMutation = useMutation({
    mutationFn: async (request: SignUpByOtpCodeRequest) => {
      const refUrl = refUrlService.get();
      const response = await authApiService.signUp(
        refUrl ? { ...request, refUrl } : request
      );
      if (!response.isSuccess) {
        return response;
      }

      await firebaseAuthService.signInWithCustomToken(response.token);
      signIn(response.user);

      trackingService.identify(response.user.id);
      trackingService.trackEvent('sign_in');

      // Registration: fan out to PostHog (product) and, via GtmTracker's
      // marketing map, to GTM (GA4 sign_up → Meta/TikTok CompleteRegistration).
      // Only for genuinely new users, not returning users on the same OTP flow.
      if (response.isNew) {
        trackingService.trackEvent('sign_up');
      }

      if (refUrl) {
        // Clear before the async send — useCampaignTracking fires once
        // isAuthenticated flips and would otherwise re-send the same URL.
        // Fire-and-forget: campaign tracking must not block or fail auth.
        refUrlService.clear();
        campaignApiService.track({ refUrl }).catch(() => {});
      }

      return response;
    },
  });

  const requestOtp = async (request: RequestOtpCodeRequest) => {
    return await requestOtpMutation.mutateAsync(request);
  };

  const signUp = async (request: SignUpByOtpCodeRequest) => {
    return await signUpMutation.mutateAsync(request);
  };

  const handleLogout = async (): Promise<void> => {
    trackingService.trackEvent('sign_out');
    await firebaseAuthService.signOut();
    queryClient.clear(); // nukes query + mutation caches
    await stopConnection();
    logout();
    trackingService.reset();
  };

  const requestAppDownloadLinkMutation = useMutation({
    mutationFn: (request: RequestAppLinkRequest) =>
      authApiService.requestAppDownloadLink(request),
  });

  return {
    isAuthenticated,
    isLoading,
    logout: handleLogout,
    requestOtp,
    signUp,
    setAuthPhoneNumber,
    authPhoneNumber,
    isRequestingOtp: requestOtpMutation.isPending,
    isSigningUp: signUpMutation.isPending,
    requestOtpError: requestOtpMutation.error,
    signUpError: signUpMutation.error,
    requestAppDownloadLink: requestAppDownloadLinkMutation.mutateAsync,
  };
};
